import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import type { SubscriptionResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

type Callbacks = {
  onSuccess?: (response: SubscriptionResponse) => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
};

/** Response-bearing payment calls stay on the component's private stack.
 * React Query stores neither first-invoice secrets nor their mutation results.
 */
export function useEphemeralSubscriptionMutation<V>(
  request: (variables: V) => Promise<SubscriptionResponse>,
  target: (variables: V, response?: SubscriptionResponse) => string | undefined,
) {
  const cache = useQueryClient();
  const { user } = useAuth();
  const actor = useRef(user?.uid);
  actor.current = user?.uid;
  const lifecycle = useRef(0);
  const busy = useRef(false);
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(
    () => () => {
      lifecycle.current += 1;
    },
    [],
  );

  async function execute(variables: V, callbacks?: Callbacks) {
    if (busy.current || !actor.current)
      throw new Error("Payment confirmation is unavailable");
    const originalActor = actor.current;
    const generation = lifecycle.current;
    const current = () =>
      generation === lifecycle.current && actor.current === originalActor;
    let response: SubscriptionResponse | undefined;
    busy.current = true;
    setPending(true);
    setError(null);
    try {
      response = await request(variables);
      if (current()) callbacks?.onSuccess?.(response);
      return response;
    } catch (failure) {
      if (current()) {
        setError(failure);
        callbacks?.onError?.(failure);
      }
      throw failure;
    } finally {
      const id = target(variables, response);
      // Settlement must refresh even on failure; only the exact old actor/id
      // is invalidated, never another membership's detail or private balance.
      await Promise.all([
        cache.invalidateQueries({ queryKey: queryKeys.memberships.mine() }),
        cache.invalidateQueries({
          queryKey: queryKeys.memberships.visitBalances(),
        }),
        ...(id
          ? [
              cache.invalidateQueries({
                queryKey: [...queryKeys.memberships.detail(id), originalActor],
                exact: true,
              }),
              cache.invalidateQueries({
                queryKey: [
                  ...queryKeys.memberships.visitBalance(id),
                  originalActor,
                ],
                exact: true,
              }),
            ]
          : []),
      ]);
      busy.current = false;
      if (current()) {
        setPending(false);
        callbacks?.onSettled?.();
      }
    }
  }
  return {
    isPending,
    error,
    isError: error !== null,
    mutate: (variables: V, callbacks?: Callbacks) => {
      void execute(variables, callbacks).catch(() => {});
    },
    mutateAsync: (variables: V) => execute(variables),
    reset: () => setError(null),
  };
}
